import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSet } from "react-use";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { BusinessReport, BusinessReportPrompt } from "@/modules/snapshots/businessReports.types";
import { BusinessReportDetailsRow } from "../generate/businessReportDetailsRow";

interface Props {
  businessReport: BusinessReport;
  prompts: BusinessReportPrompt[];
  setPrompts: React.Dispatch<React.SetStateAction<BusinessReportPrompt[]>>;
}

export const BusinessReportDetails: React.FC<Props> = ({ setPrompts, prompts, businessReport }) => {
  const { formatDayMonth, formatTime } = useDateFormatter();

  const formatDateTime = (dateString: string) => `${formatDayMonth(dateString)}, ${formatTime(dateString)}`;
  const [expandedItems, { toggle, clear, add }] = useSet<string>(new Set());

  const isAllExpanded = prompts.every(p => expandedItems.has(p.id));

  const toggleAll = () => {
    if (isAllExpanded) {
      clear();
    } else {
      prompts.forEach(p => add(p.id));
    }
  };

  return (
    <>
      <div className="mb-4">
        <h2 className="text-xl font-bold leading-tight text-[#111b35]">{businessReport.name}</h2>
        {!!businessReport.variables.length && (
          <p className="mt-1 text-sm font-medium text-gray-500">Variables are required</p>
        )}
      </div>

      <dl className="grid overflow-hidden rounded-lg border border-[#e4ddd5] bg-[#f7f2ec] sm:grid-cols-2">
        <div className="min-w-0 border-b border-[#e4ddd5] px-5 py-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Created</dt>
          <dd className="mt-1 truncate text-sm font-semibold text-[#111b35]">
            {formatDateTime(businessReport.created_at)}
          </dd>
        </div>
        {businessReport.last_used_at && (
          <div className="min-w-0 px-5 py-4">
            <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Last Used</dt>
            <dd className="mt-1 truncate text-sm font-semibold text-[#111b35]">
              {formatDateTime(businessReport.last_used_at)}
            </dd>
          </div>
        )}
      </dl>

      <div className="mb-3 mt-6 flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#111b35]">
          Prompts <span className="font-medium text-gray-500">({prompts.length})</span>
        </h3>
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" onClick={toggleAll}>
          {isAllExpanded ? (
            <>
              <ChevronUp className="size-4" />
              Collapse all
            </>
          ) : (
            <>
              <ChevronDown className="size-4" />
              Expand all
            </>
          )}
        </Button>
      </div>

      <div className="space-y-3">
        {prompts.map(prompt => (
          <BusinessReportDetailsRow
            key={prompt.id}
            prompt={prompt}
            setPrompts={setPrompts}
            isExpanded={expandedItems.has(prompt.id)}
            onToggle={() => toggle(prompt.id)}
            templateType={businessReport.type}
          />
        ))}
      </div>
    </>
  );
};
